import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { useNavigate } from 'react-router-dom';
import { sanitizeInput, formatCuit } from '../../utils/inputValidation';
import { getInfoStoredScoring } from '../../services/scoringService';
import Swal from 'sweetalert2';

const BusquedaCasos = ({ user, onLogout, onNavigate }) => {
  const navigate = useNavigate();
  const [grupo, setGrupo] = useState('');
  const [orden, setOrden] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [hasBuscado, setHasBuscado] = useState(false);

  // Función para formatear fecha de yyyy-mm-dd a dd-mm-yyyy
  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') {
      return 'N/A';
    }
    
    try {
      // Si ya está en formato dd-mm-yyyy o dd/mm/yyyy, convertir
      if (fecha.includes('/')) {
        const partes = fecha.split('/');
        if (partes.length === 3) {
          const [dia, mes, anio] = partes;
          return `${dia}-${mes}-${anio}`;
        }
      }
      
      // Si está en formato yyyy-mm-dd, convertir a dd-mm-yyyy
      if (fecha.includes('-')) {
        const partes = fecha.split('-');
        if (partes.length === 3) {
          // Verificar si es yyyy-mm-dd o dd-mm-yyyy
          if (partes[0].length === 4) {
            // Es yyyy-mm-dd
            const [anio, mes, dia] = partes;
            return `${dia}-${mes}-${anio}`;
          } else {
            // Ya está en dd-mm-yyyy
            return fecha;
          }
        }
      }
      
      return fecha;
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return fecha;
    }
  };

  // Función para mapear un caso de la API al formato esperado
  const mapearCaso = (item) => {
    const fechaAceptacion = item.planData?.acceptDate || item.acceptDate || 'N/A';
    return {
      id: item.id,
      grupo: item.group || item.grupo,
      orden: item.order || item.orden,
      titular: item.titularData?.completeName || item.mainName || item.titular || 'N/A',
      cuit: item.titularData?.documentNumber || item.cuil || item.cuit || 'N/A',
      aceptacion: formatearFecha(fechaAceptacion),
      deuda: item.planData?.debtPercent ? `${item.planData.debtPercent}%` : item.debtPercent ? `${item.debtPercent}%` : '0%',
      concesionario: item.planData?.sellerHouse?.ceName || item.sellerHouse?.ceName || item.concesionario || 'N/A',
      estado: item.status || item.estado || 'N/A',
    };
  };

  const handleBuscar = async () => {
    // Validar que se proporcionen ambos campos
    if (!grupo || !orden) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor, ingrese tanto el grupo como el orden para realizar la búsqueda.',
        confirmButtonColor: '#ef4444',
      });
      return;
    }
    
    try {
      setBuscando(true);
      setHasBuscado(true);
      
      // Llamar al endpoint GetInfoStoredScoring
      const data = await getInfoStoredScoring(grupo, orden, (user.ce_ID !== null ? user.ce_ID : 0));
      
      // Mapear el resultado al formato esperado
      const casoMapeado = mapearCaso(data);
      
      // Establecer resultados (array con un solo elemento)
      setResultados([casoMapeado]);
      
    } catch (error) {
      console.error('Error al buscar caso:', error);
      
      // Mostrar error al usuario
      Swal.fire({
        icon: 'error',
        title: 'Error en la búsqueda',
        text: error.message || 'No se pudo encontrar el caso solicitado. Verifique los datos ingresados.',
        confirmButtonColor: '#ef4444',
      });
      
      // Limpiar resultados en caso de error
      setResultados([]);
    } finally {
      setBuscando(false);
    }
  };

  const handleVerMas = (caso) => {
    // Preparar datos completos del caso para VisualizacionExpediente
    const casoData = {
      grupo: caso.grupo,
      orden: caso.orden,
      titular: caso.titular,
      cuil: caso.cuit,
      aceptacion: caso.aceptacion,
      concesionario: caso.concesionario,
      porcentajeDeuda: caso.deuda,
      estado: caso.estado,
      idScoring: caso.id
    };
    
    // Guardar en localStorage para que VisualizacionExpediente lo recupere
    localStorage.setItem('casoData', JSON.stringify(casoData));
    
    // Redirigir a visualizacionExpediente
    if (onNavigate) {
      onNavigate('visualizacionExpediente', { 
        id: caso.id, 
        grupo: caso.grupo, 
        orden: caso.orden 
      });
    } else {
      navigate('/visualizacion-expediente');
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-3 py-4">
        {/* Título */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">
              Busqueda de Casos
            </h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Busque casos de scoring ingresando el grupo y orden</p>
        </div>

        {/* Formulario de búsqueda */}
        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-20 blur-3xl"></div>
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Grupo</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ingrese número de grupo"
                    value={grupo}
                    onChange={(e) => {
                      const sanitized = sanitizeInput(e.target.value);
                      setGrupo(sanitized);
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 group-hover:border-gray-300"
                  />
                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">tag</span>
                </div>
              </div>
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Orden</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ingrese número de orden"
                    value={orden}
                    onChange={(e) => {
                      const sanitized = sanitizeInput(e.target.value);
                      setOrden(sanitized);
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 group-hover:border-gray-300"
                  />
                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">receipt_long</span>
                </div>
              </div>
              <div>
                <button
                  onClick={handleBuscar}
                  disabled={buscando}
                  className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all duration-200 text-sm font-semibold shadow-lg shadow-red-200 hover:shadow-xl hover:shadow-red-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-icons text-sm">{buscando ? 'hourglass_empty' : 'search'}</span>
                  {buscando ? 'Buscando...' : 'Buscar Caso'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de resultados */}
        {buscando ? (
          <HondaLoader 
            message="Buscando información..." 
            variant="inline"
            size="medium"
            subtitle="Estamos procesando su solicitud y recuperando los casos solicitados"
          />
        ) : !hasBuscado ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <span className="material-icons text-5xl text-gray-300 mb-3 block">search</span>
            <p className="text-gray-400">No hay datos disponibles</p>
            <p className="text-gray-300 text-sm mt-1">
              Ingrese el grupo y orden, luego presione "Buscar Caso"
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden animate-fade-in">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-red-600 to-red-700">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Grupo</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Orden</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Titular</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">DNI / CUIT</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Aceptación</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">% de Deuda</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Concesionario</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {resultados.length > 0 ? (
                    resultados.map((caso) => (
                      <tr key={caso.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900">{caso.grupo}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{caso.orden}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{caso.titular}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatCuit(caso.cuit)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{caso.aceptacion}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs ${
                            parseFloat(caso.deuda.replace(',', '.')) > 66 ? 'bg-red-100 text-red-700' :
                            parseFloat(caso.deuda.replace(',', '.')) > 33 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {caso.deuda}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={caso.concesionario}>
                          {caso.concesionario}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {caso.estado}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleVerMas(caso)}
                            className="text-blue-600 hover:bg-blue-600 hover:text-white transition-colors p-1 rounded-full"
                            title="Ver más información"
                          >
                            <span className="material-icons text-xl">info</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="px-4 py-12 text-center">
                        <span className="material-icons text-5xl text-gray-300 mb-3 block">search</span>
                        <p className="text-gray-400">No hay datos disponibles en la tabla</p>
                        <p className="text-gray-300 text-sm mt-1">
                          No se encontraron resultados para la búsqueda realizada
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
  
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BusquedaCasos;

